/**
 * Care & Painting Guide Page
 */

import { ContentBlock, Box } from 'braid-design-system';
import type React from 'react';

import { PageHead } from '../../components/PageHead/PageHead';

import * as styles from './CareGuide.css';

const sections = [
  {
    title: 'Caring for hand-painted pieces',
    body: 'Dust gently with a soft brush. Store unpainted seasonal pieces in a dry box when not on display.',
  },
  {
    title: 'Touching up chips or scratches',
    body: 'All pieces are damage resistant and can handle drops from a low height. However, they are not indestructible! Damage from drops or knocks can be touched up with matching acrylics and re-sealed with a clear coat.',
  },
  {
    title: 'Weather protection for outdoor pieces',
    body: 'Weather is the bane of all outdoor pieces. Ensure your pieces are protected from direct sun during extreme heat. Warping will occur if left in the summer sun against a brick wall, for example.',
  },
  {
    title: 'Cleaning raw 3D prints',
    body: 'Raw 3D prints have been washed and lightly sanded before shipping. For further surface preparation before painting, a light sanding with a high-grit sandpaper to smooth any further rough surfaces or edges should be all that is required.',
  },
  {
    title: 'Priming',
    body: 'Apply a thin coat of plastic primer or gesso in a well-ventilated area. Two light coats give better results than one heavy coat. Let each layer dry completely before painting.',
  },
  {
    title: 'Painting',
    body: 'Acrylic craft paints work beautifully on fairy garden miniatures. Build colour in thin layers, starting with base coats and adding dry-brushed highlights for bark, stone, and leaf textures.',
  },
  {
    title: 'Sealing for outdoor use',
    body: 'Finish with a matte or satin UV-resistant clear coat. Reapply annually for pieces in full sun or heavy rain.',
  },
];

export const CareGuide: React.FC = () => (
  <ContentBlock width="large">
    <PageHead
      title="Care & Painting Guide"
      description="Tips for printing, painting, and caring for Fae Forest fairy garden miniatures indoors and outdoors."
      path="/care-guide"
    />
    <Box paddingY="large">
      <article className={styles.container}>
        <h1 className={styles.heading}>Care & Painting Guide</h1>
        <p className={styles.lead}>
          Whether you print at home or display one of our hand-painted pieces,
          these tips will help your fairy garden stay magical for years.
        </p>
        {sections.map((section) => (
          <section key={section.title} className={styles.section}>
            <h2 className={styles.sectionTitle}>{section.title}</h2>
            <p className={styles.body}>{section.body}</p>
          </section>
        ))}
      </article>
    </Box>
  </ContentBlock>
);
